import React, { useEffect, useMemo, useState } from "react"
import { Image as RNImage } from "~/ui/primitives"
import FastImage from "~/ui/FastImage"
import useNavigation from "~hooks/useNavigation"
import { Message } from "../../models"
import { getMediaPath } from "../../utils"
import { MessageTypes } from "~proto"
import { buildGalleryImageKey } from "~utils/gallery-image"
import { fitContainSize, getChatMediaMaxBounds, CHAT_IMAGE_MAX_WIDTH } from "~utils/media-fit"
import Progress from "./progress"
import styled from '~/ui/styled'
import { MessageStatus } from "~proto"
import { hapticTrigger } from "../../utils/haptic"
import { observer } from "mobx-react-lite"
import MessageSent from "./message-sent"
import MessageReceived from "./message-received"
import MessageTime from "./message-time"
import { MessageProps } from "./message.props"
import { ColumnFlex } from "~components/El"

const Container = styled.div`
  /* width: ${({ theme }) => `${theme.window.width * 0.4}px`}; */
  height: auto;
  width: auto;
`

const ImageContainer = styled(ColumnFlex)`
  border-radius: 6px;
  width: fit-content;
  max-width: 100%;
  height: auto;
  justify-content: flex-end;
`

const ImageMessageTouchableWrap = styled.Pressable``

const TimeWrap = styled.View<{ byMe: boolean }>`
  width: 100%;
  align-items: ${({ byMe }) => (byMe ? "flex-end" : "flex-start")};
  margin-top: 4px;
  padding-left: 2px;
  padding-right: 2px;
`

interface ImageMessageProps {
  message: Message
  messageHack?: Partial<Message>
  setPopupOptionListModal?: ({ visible, message }: { visible: boolean; message: Message }) => void
  /** 多图消息在父级统一展示时间时为 false */
  showTime?: boolean
  /** 图组内单张最大宽度（不传则用单图上限） */
  maxDisplayWidth?: number
}

// 优化使用纯组件
export const ImageMessage: React.FC<ImageMessageProps> = observer(
  ({ message: pMessage, messageHack, setPopupOptionListModal, showTime = true, maxDisplayWidth }) => {
    const message = {
      ...pMessage,
      ...(messageHack || {}),
    }
    const image = getMediaPath(message)
    const navigation = useNavigation()

    const [loading, setLoading] = useState<boolean>(false)
    const [progress, setProgress] = useState<number>(0)
    const [intrinsic, setIntrinsic] = useState<{ w: number; h: number } | null>(null)

    const { maxWidth: boundsMaxW, maxHeight: maxH } = getChatMediaMaxBounds()
    const maxW = Math.min(maxDisplayWidth ?? CHAT_IMAGE_MAX_WIDTH, boundsMaxW)

    const { displayW, displayH } = useMemo(() => {
      if (!intrinsic || intrinsic.w <= 0 || intrinsic.h <= 0) {
        return { displayW: maxW, displayH: maxW }
      }
      return fitContainSize(intrinsic.w, intrinsic.h, maxW, maxH)
    }, [intrinsic, maxW, maxH])

    useEffect(() => {
      // image 随 message 变化（含 recycleItems 复用），重置尺寸/加载状态，避免串台
      setIntrinsic(null)
      setLoading(false)
      setProgress(0)
      if (!image) return undefined
      // FastImage 在 Android 上 onLoad 不会回传 width/height，
      // 改用 RN 内置 Image.getSize 取原始宽高，iOS / Android 都可用。
      let cancelled = false
      RNImage.getSize(
        image,
        (w, h) => {
          if (!cancelled && w > 0 && h > 0) {
            setIntrinsic({ w, h })
          }
        },
        () => {
          // 取尺寸失败时保持默认占位尺寸，避免阻塞渲染
        },
      )
      return () => {
        cancelled = true
      }
    }, [image])

    // 下载进度条提示
    const onProgress = ({ nativeEvent: { loaded, total } }) => {
      setProgress(loaded / total)
    }

    // 加载图片失败的处理, TODO
    const onError = () => {
      //
    }

    // 开始加载图片
    const onLoadStart = () => {
      setLoading(true)
    }

    // 加载结束， 不管是否成功或失败
    const onLoadEnd = () => {
      setLoading(false)
    }

    // 选中图片进行预览
    const onSelectImage = () => {
      if (
        ![MessageStatus.SENT, MessageStatus.DELIVERED, MessageStatus.READ].includes(message.status)
      )
        return

      navigation.navigate("ImageGallery", {
        images: [
          {
            type: MessageTypes.IMAGE,
            message_id: message.message_id,
            attach_path: message.attach_path,
            url: messageHack?.url,
            content: message.content,
            key: buildGalleryImageKey({
              message_id: message.message_id,
              attach_path: message.attach_path,
              url: messageHack?.url,
              content: message.content,
            }),
          },
        ],
        index: 0,
      })
    }

    const handleOnLongPress = () => {
      if (setPopupOptionListModal) {
        hapticTrigger()
        setPopupOptionListModal({ visible: true, message })
      }
    }

    const imgStyle = {
      width: displayW,
      height: displayH,
      maxWidth: maxW,
      display: "block" as const,
    }

    const onLoad = ({ nativeEvent: { width: iw, height: ih } }: {
      nativeEvent: { width: number; height: number }
    }) => {
      if (iw > 0 && ih > 0) {
        setIntrinsic({ w: iw, h: ih })
      }
    }

    return (
      <ImageContainer>
        <ImageMessageTouchableWrap onLongPress={handleOnLongPress} onClick={onSelectImage}>
          {!message.isUploading && <Progress progress={progress} inProgress={loading} size={50} />}
          <FastImage
            style={imgStyle}
            src={image}
            resizeMode={FastImage.resizeMode.cover}
            onProgress={onProgress}
            onError={onError}
            onLoad={onLoad}
            onLoadEnd={onLoadEnd}
            onLoadStart={onLoadStart}
          />
          <Progress progress={message.progress} inProgress={message.isUploading} size={50} />
        </ImageMessageTouchableWrap>
        {showTime ? (
          <TimeWrap byMe={message.byMe}>
            <MessageTime time={message.created} />
          </TimeWrap>
        ) : null}
      </ImageContainer>
    )
  },
)
export const ImageMessageWithContainer: React.FC<ImageMessageProps> = (props) => {
  return (
    <Container>
      <ImageMessage {...props} />
    </Container>
  )
}
export const WrappedImageMessage: React.FC<MessageProps & ImageMessageProps> = (props) => {
  return props.message.byMe ? (
    <MessageSent {...props} Component={ImageMessageWithContainer} isNoBg={true} />
  ) : (
    <MessageReceived {...props} Component={ImageMessageWithContainer} isNoBg={true} />
  )
}
export default WrappedImageMessage
